var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { BridgeType, PresaleContractType } from '@cta-portal/webapp-common/lib/api/schema';
import { useI18n } from '@cta-portal/webapp-common/lib/hooks/translations/use-i18n';
import Loader from '@cta-portal/webapp-common/src/components/loader';
import { useContext, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { AccountTab, RoutePath, generateRoutePath } from '../../app/router/config';
import bridgeIcon from '../../assets/images/icons/common/icon-bridge-purple.png';
import Header from '../../components/bridge/header';
import Table, { CellAlign, CellType } from '../../components/common/table';
import { DialogContext } from '../../context/dialog';
import { UserContext } from '../../context/user';
import { PresalePackTokenBurnStatus, useBridge } from '../../hooks/bridge/use-bridge';
import { useNft } from '../../hooks/bridge/use-nft';
import Layout from '../../layout';
import { DialogType } from '../../types/dialogs';
export default function Bridge() {
    var t = useI18n().t;
    var _a = useParams(), bridgeType = _a.bridgeType, contractType = _a.contractType;
    var navigate = useNavigate();
    var setDialogProps = useContext(DialogContext).setDialogProps;
    var user = useContext(UserContext).user;
    var _b = useBridge(), items = _b.items, metamaskConnected = _b.metamaskConnected, metamaskWalletAddress = _b.metamaskWalletAddress, initialized = _b.initialized, metamaskReady = _b.metamaskReady, isMetamaskRequestPending = _b.isMetamaskRequestPending, connectMetamask = _b.connectMetamask, connectWalletHandler = _b.connectWalletHandler;
    var _c = useNft(bridgeType, contractType, metamaskReady), packs = _c.packs, nfts = _c.nfts, errorMessage = _c.errorMessage, ntfInitialized = _c.initialized, showConfirmBridgeDialog = _c.showConfirmBridgeDialog;
    useEffect(function () {
        if (!(user === null || user === void 0 ? void 0 : user.magicLinkWalletAddress)) {
            setDialogProps({
                common: {
                    title: t('common.error'),
                    actions: [
                        {
                            text: t('common.back'),
                            link: generateRoutePath(RoutePath.ACCOUNT, { tab: AccountTab.TOOLS }),
                            alternative: true,
                        },
                    ],
                },
                custom: {
                    type: DialogType.SIMPLE,
                    message: t('bridge.magic-link-wallet-undefined'),
                },
            });
            return;
        }
        if (errorMessage) {
            setDialogProps({
                common: {
                    title: t('common.error'),
                    actions: [
                        {
                            text: t('common.back'),
                            link: generateRoutePath(RoutePath.ACCOUNT, { tab: AccountTab.TOOLS }),
                            alternative: true,
                        },
                    ],
                },
                custom: {
                    type: DialogType.SIMPLE,
                    message: errorMessage,
                },
            });
            return;
        }
        if (!metamaskConnected) {
            setDialogProps({
                common: {
                    title: t('bridge.connect-metamask.title'),
                    actions: [
                        {
                            text: t('common.back'),
                            link: generateRoutePath(RoutePath.ACCOUNT, { tab: AccountTab.TOOLS }),
                            alternative: true,
                        },
                        { text: t('common.connect'), highlighted: true, action: connectMetamask },
                    ],
                },
                custom: {
                    type: DialogType.SIMPLE,
                    message: t('bridge.connect-metamask.message'),
                },
            });
            return;
        }
        if (!(user === null || user === void 0 ? void 0 : user.polygonWalletConfirmed) ||
            ((user === null || user === void 0 ? void 0 : user.polygonWalletId) &&
                metamaskWalletAddress &&
                metamaskWalletAddress.toLowerCase() !== user.polygonWalletId.toLowerCase())) {
            setDialogProps({
                common: {
                    title: t('bridge.link-wallet.title'),
                    actions: [
                        {
                            text: t('common.back'),
                            link: generateRoutePath(RoutePath.ACCOUNT, { tab: AccountTab.TOOLS }),
                            alternative: true,
                        },
                        { text: t('common.link-wallet'), highlighted: true, action: connectWalletHandler },
                    ],
                },
                custom: {
                    type: DialogType.SIMPLE,
                    message: t('bridge.link-wallet.message'),
                },
            });
            return;
        }
        setDialogProps(undefined);
    }, [user, metamaskConnected, metamaskWalletAddress, errorMessage]);
    function getStatus(tokenId) {
        var pack = packs.find(function (pack) { return pack.tokenId === tokenId; });
        return pack ? pack.status : PresalePackTokenBurnStatus.TO_DO;
    }
    if (!bridgeType || !Object.values(BridgeType).includes(bridgeType)) {
        navigate(generateRoutePath(RoutePath.ACCOUNT, { tab: AccountTab.TOOLS }));
        return;
    }
    if (!contractType || !Object.values(PresaleContractType).includes(contractType)) {
        navigate(generateRoutePath(RoutePath.ACCOUNT, { tab: AccountTab.TOOLS }));
        return;
    }
    var currentItem = items.find(function (item) { return item.bridgeType === bridgeType && item.contractType === contractType; });
    if (!currentItem) {
        navigate(generateRoutePath(RoutePath.ACCOUNT, { tab: AccountTab.TOOLS }));
        return;
    }
    var labels = [
        { key: 'image', label: '', align: CellAlign.CENTER, size: 0.5, hiddenMobile: true },
        { key: 'name', label: t('common.name'), align: CellAlign.LEFT, size: 1.5, mobileSize: 1 },
        { key: 'tokenId', label: t('common.token-id'), align: CellAlign.CENTER, size: 1.5, mobileSize: 1 },
        { key: 'status', label: t('common.status'), align: CellAlign.CENTER },
        { key: 'action', label: t('common.action'), align: CellAlign.CENTER, actionCell: true },
    ];
    var rows = nfts.map(function (nft) { return ({
        id: nft.tokenId,
        data: {
            image: { type: CellType.IMAGE, value: currentItem.image },
            name: { type: CellType.TEXT, value: currentItem.label },
            tokenId: { type: CellType.NUMBER, value: nft.tokenId },
            status: { type: CellType.STATUS, value: getStatus(nft.tokenId) },
        },
    }); });
    var actions = [
        {
            label: t('common.bridge'),
            icon: bridgeIcon,
            action: showConfirmBridgeDialog,
            showDisabled: true,
            generateDisability: function (id) {
                var status = getStatus(id);
                return status !== PresalePackTokenBurnStatus.TO_DO;
            },
        },
    ];
    return (_jsx(Layout, { children: currentItem && (_jsxs(_Fragment, { children: [_jsx(Header, { image: currentItem.image, single: currentItem.bridgeType !== BridgeType.BORED_BOX &&
                        (currentItem.contractType === PresaleContractType.STANDARD ||
                            currentItem.contractType === PresaleContractType.LEGACY), title: "".concat(t('common.bridge'), " ").concat(currentItem.label) }), (!initialized || isMetamaskRequestPending || (metamaskReady && !ntfInitialized)) && _jsx(Loader, { black: true }), _jsx(Container, { children: ntfInitialized && _jsx(Table, { actions: actions, labels: labels, rows: rows }) })] })) }));
}
var Container = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  align-items: center;\n  display: flex;\n  flex-direction: column;\n  justify-content: flex-start;\n  padding: 40px 64px;\n  width: 100%;\n\n  @media screen and (max-width: 824px) {\n    padding: 40px 16px;\n  }\n"], ["\n  align-items: center;\n  display: flex;\n  flex-direction: column;\n  justify-content: flex-start;\n  padding: 40px 64px;\n  width: 100%;\n\n  @media screen and (max-width: 824px) {\n    padding: 40px 16px;\n  }\n"])));
var templateObject_1;
